import { L } from 'harmony-language';
import React from 'react';
import { LoadCargo2 } from '../shared/load-table/load-table-cells';
import { getTransLocationTypeLabel } from '../../constants/constants';
import { Skeleton, Typography } from '@mui/material';
import { localTime } from '../../utils/date-time-utils';
import { convertSpeedToDisplay } from '../../utils/unit-conversion';
import { useOrganizationQuery } from '../../api/queries/use-organization-query';
import { DeviceLocation, Driver, Load, OrganizationLocation } from '../../types';
import { OrgQueryKeys } from '../../api/config';
import { useWeights } from '../shared/hooks/use-weights';
import { useUser } from '../../api/queries/use-user';
import { getNextStop } from './utils/get-next-stop';
import { useTractorTrailer } from '../../api/queries/use-tractor-trailer';
import { getGrossWeight } from '../shared/load-table/utils/load-utils';
import { toSiteDisplayName } from '../../utils/data-mapping';

interface LiveMapDriverDetailsProps {
    load: Load;
    deviceLocation: DeviceLocation;
}

export const LiveMapDriverDetails = (props: LiveMapDriverDetailsProps) => {
    const { load, deviceLocation } = props;
    const { data: drivers, isLoading: isLoadingDrivers } = useOrganizationQuery<Driver[]>(OrgQueryKeys.drivers);
    const { tractors, trailers, isLoading: isLoadingEquipment } = useTractorTrailer();
    const { data: organizationLocations, isLoading: isLoadingOrganizationLocations } = useOrganizationQuery<OrganizationLocation[]>(OrgQueryKeys.locations, { disableOnFreemium: true });
    const { user } = useUser();
    const { convertFromGramsDisplay } = useWeights();

    const driver = drivers?.find(x => x.id === deviceLocation.userId);
    const tractor = tractors?.find(t => t.id === deviceLocation.tractorId);
    const trailer = trailers?.find(t => t.id === deviceLocation.trailerId);

    const unitSystem = user.contact?.units || user.organization?.units;
    const grossWeightObj = getGrossWeight(tractor, trailer, load?.stops, convertFromGramsDisplay);
    
    const nextStop = getNextStop(load.stops);
    const stopLocation = organizationLocations?.find(x => x.id === nextStop?.organizationLocationId);
    const displayName = stopLocation ? toSiteDisplayName(stopLocation.name, stopLocation.description) : '';
    if (nextStop) {
        nextStop.organizationLocation = stopLocation;
        nextStop.displayName = displayName;
    }

    return (
        <>
            <div className='live-map-driver-details'>
                <Typography className='section-title'>{L.driver()}</Typography>
                <Typography>{!isLoadingDrivers ? driver?.name : <Skeleton />}</Typography>
                <Typography>{!isLoadingDrivers ? driver?.phone : <Skeleton />}</Typography>
            </div>
            <div className='live-map-driver-details'>
                <span className='section-title'>{`${L.load()} ${load.id}`}</span>
                {!isLoadingEquipment ? <LoadCargo2 load={load} convertFromGramsDisplay={convertFromGramsDisplay} /> : <Skeleton />}
            </div>
            {nextStop && <>
                <div className='live-map-driver-details'>
                    <span className='section-title'>{L.nextStop()}</span>
                    <span>{!isLoadingOrganizationLocations ? nextStop?.displayName : <Skeleton />}</span>
                    <span>
                        {!isLoadingOrganizationLocations ? getTransLocationTypeLabel(nextStop?.organizationLocation?.organizationLocationTypeId) : <Skeleton />}
                    </span>
                <div>
                    <Typography variant='subtitle2'>{!isLoadingOrganizationLocations ? nextStop?.organizationLocation?.address1 : <Skeleton />}</Typography>
                    <Typography variant='subtitle2'>{!isLoadingOrganizationLocations ? `${nextStop?.organizationLocation?.city}, ${nextStop?.organizationLocation?.state} ${nextStop?.organizationLocation?.postalCode}` : <Skeleton />}</Typography>
                    <Typography variant='subtitle2'>{!isLoadingOrganizationLocations ? L.dockTime(localTime(nextStop?.arrivalTime)) : <Skeleton />}</Typography>
                </div>
                </div>
            </>}
            <div className='live-map-driver-details'>
                <span className='section-title'>{L.speed()}</span>
                <span>{deviceLocation.speed !== null ? convertSpeedToDisplay(unitSystem)(deviceLocation.speed) : L.unavailable()}</span>
            </div>
            <div className='live-map-driver-details'>
                <span className='section-title'>{L.weight()}</span>
                <span>{!isLoadingEquipment ? (grossWeightObj?.grossWeight || L.unavailable()) : <Skeleton />}</span>
            </div>
        </>
    )
}